<template>
  <section class="section-base form-base">
    <template v-if="loaded">
      <AddStatementHeader
        :title="RouteText.RESEND_TO_FSSP"
        :steps-count="RESEND_TO_FSSP_STEPS_COUNT"
        :current-step="1"
      />

      <AddStBaseApplicant
        :add-statement="addStatement"
        @send="btnNextClickHandler"
      >
        <template #footer="{ next }">
          <Footer
            :loading-next="isSaveDraftLoading"
            first-step
            @next-btn-click="next"
            class="pt-10"
          />
        </template>
      </AddStBaseApplicant>

      <v-dialog v-model="isDialogArchiveOpen" max-width="600px">
        <div class="form">
          <div class="form-base__title mb-4">{{ dialogArchiveTitle }}</div>

          <p>
            Если вы собираетесь заново начать работу над заявлением, то ранее
            внесенные изменения будут удалены.
          </p>

          <v-row justify="end">
            <v-col cols="auto">
              <v-btn
                @click="openDialogDelete"
                depressed
                color="primary"
                class="button-base"
              >
                Начать заново
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                @click="
                  goToSign();
                  closeDialogArchive();
                "
                :loading="isArchiveFormedLoading"
                depressed
                color="primary"
                class="button-base"
              >
                Продолжить работу
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-dialog>

      <v-dialog persistent v-model="isDialogDeleteOpen" max-width="600px">
        <div class="form">
          <div class="form-base__title mb-4">Подтверждение</div>

          <div class="form-base__title mb-4">
            Вы действительно хотите удалить ранее внесенные изменения?
          </div>

          <p>Отменить это действие будет невозможно.</p>

          <v-row justify="end">
            <v-col cols="auto">
              <v-btn
                depressed
                color="primary"
                class="button-base"
                @click="closeDialogDelete"
              >
                Нет
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                depressed
                color="primary"
                class="button-base"
                :loading="isDeleteStatementLinkLoading"
                @click="btnDeleteClickHandler"
              >
                Да
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-dialog>
    </template>
    <SkeletonLoader v-if="loading" />
    <SignatureComponent />
  </section>
</template>

<script>
import { RouteName, RouteText } from '@/router';
import { EditedStatementModule } from '@/store/modules/alternativeServices/editedStatement';
import { RecendStatementModule } from '@/store/modules/alternativeServices/recendStatement';
import Footer from '@/components/services/elements/Footer';
import AddStBaseApplicant from '@/views/alternativeServices/AddStApplicant/AddStBaseApplicant';
import SkeletonLoader from '@/components/base/SkeletonLoader';
import AddStatementHeader from '@/views/alternativeServices/AddStatementHeader';
import { RESEND_TO_FSSP_STEPS_COUNT } from '@/constants';
import { BreadCrumbsModule } from '@/store/modules/breadCrumbs';
import { getResendToFsspBreadcrumbs } from '@/utils/common';
import SignatureComponent from '@/components/base/SignatureComponent';
import { Role } from '@/utils/dictionaries';

export default {
  name: 'ResendToFsspApplicant',
  components: {
    Footer,
    AddStBaseApplicant,
    SkeletonLoader,
    AddStatementHeader,
    SignatureComponent,
  },
  props: {
    altStatementId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    RouteText,
    RESEND_TO_FSSP_STEPS_COUNT,
    isDialogArchiveOpen: false,
    isDialogDeleteOpen: false,
    archiveFormedInterval: null,
  }),
  computed: {
    isArchiveStartedFormed() {
      return this.addStatement.changedStatementZipLink;
    },

    isArchiveFormed() {
      return EditedStatementModule.isArchiveFormed.entity;
    },

    isArchiveFormedLoading() {
      return EditedStatementModule.isArchiveFormed.loading;
    },

    isDeleteStatementLinkLoading() {
      return RecendStatementModule.deleteStatementLinkState.loading;
    },

    dialogArchiveTitle() {
      const unformedTitle = 'Идет загрузка архива ранее измененного заявления';
      const formedTitle = 'Архив сформирован';

      return this.isArchiveFormed ? formedTitle : unformedTitle;
    },

    isSaveDraftLoading() {
      return RecendStatementModule.saveDraftState.loading;
    },

    addStatement() {
      return RecendStatementModule.addStatement.entity;
    },

    loading() {
      return RecendStatementModule.addStatement.loading;
    },

    loaded() {
      return RecendStatementModule.addStatement.loaded;
    },

    applicant() {
      return this.addStatement?.applicant;
    },

    person() {
      return this.applicant?.person;
    },

    organization() {
      return this.applicant?.organization;
    },

    foreignOrganization() {
      return this.applicant?.foreignOrganization;
    },

    role() {
      if (this.person) return Role.PERSON;
      if (this.organization) return Role.ORGANIZATION;
      if (this.foreignOrganization) return Role.FOREIGN_ORGANIZATION;

      throw new Error('Заявитель не найден');
    },
  },
  watch: {
    isArchiveFormed(formed) {
      if (formed) this.endCheckIsArchiveFormed();
    },
  },
  async created() {
    await RecendStatementModule.getAddStatementById(this.altStatementId);

    if (this.isArchiveStartedFormed) {
      this.startCheckIsArchiveFormed();
    }

    BreadCrumbsModule.SET(
      getResendToFsspBreadcrumbs(this.addStatement.originalStatementId)
    );
  },
  beforeDestroy() {
    this.endCheckIsArchiveFormed();
  },
  methods: {
    startCheckIsArchiveFormed() {
      EditedStatementModule.checkIsArchiveFormed(
        this.addStatement.originalStatementId
      );

      this.archiveFormedInterval = setInterval(() => {
        EditedStatementModule.checkIsArchiveFormed(
          this.addStatement.originalStatementId
        );
      }, 3000);
    },

    endCheckIsArchiveFormed() {
      clearInterval(this.archiveFormedInterval);
    },

    goToEdit() {
      this.$router.push({
        name: `${RouteName.ALTERNATIVE_SERVICE_EDIT}-${
          RouteName[this.addStatement.originalStatementType]
        }`,
        params: { originalStatementId: this.addStatement.originalStatementId },
      });
    },

    openDialogArchive() {
      this.isDialogArchiveOpen = true;
    },

    closeDialogArchive() {
      this.isDialogArchiveOpen = false;
    },

    openDialogDelete() {
      this.isDialogDeleteOpen = true;
    },

    closeDialogDelete() {
      this.isDialogDeleteOpen = false;
    },

    async btnNextClickHandler(trustee) {
      await this.saveDraft(trustee);

      if (this.isArchiveStartedFormed) {
        this.openDialogArchive();
      } else this.goToEdit();
    },

    async saveDraft(trustee) {
      try {
        await RecendStatementModule.saveDraft({
          initialAddSt: this.addStatement,
          trustee,
          role: this.role,
        });
      } catch (e) {
        await this.$vuetify.goTo('#header-target', { duration: 200 });
        throw e;
      }
    },

    goToSign() {
      this.$router.push({
        name: RouteName.RESEND_TO_FSSP_SIGN,
        params: {
          altStatementId: this.altStatementId,
        },
      });
    },

    async btnDeleteClickHandler() {
      await RecendStatementModule.deleteStatementLink(this.addStatement.id);

      this.goToEdit();
    },
  },
};
</script>

<style scoped>
.form {
  padding: 24px 24px 12px;
  background-color: #fff;
}
</style>
